import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Table, Form, Alert, Badge, Spinner, Button, ButtonGroup, Card } from 'react-bootstrap';
import { format } from 'date-fns';
import { ar } from 'date-fns/locale';
import { FaFileDownload, FaFilePdf, FaPrint, FaSearch, FaChartBar } from 'react-icons/fa';
import { Course, CourseCode } from '../../types';
import { getCourseCodesUsage } from '../../services/courseService';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import html2canvas from 'html2canvas';

interface CourseCodesReportsProps {
  course: Course;
}

interface CodeUsage extends CourseCode {
  studentName?: string;
  studentEmail?: string;
  expiresAt?: number;
}

const CourseCodesReports: React.FC<CourseCodesReportsProps> = ({ course }) => {
  const [codeUsage, setCodeUsage] = useState<CodeUsage[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUsed, setFilterUsed] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [codesPerPage, setCodesPerPage] = useState<number>(10);

  // استرجاع إعدادات المستخدم من التخزين المحلي عند تحميل الصفحة
  useEffect(() => {
    const savedCodesPerPage = localStorage.getItem('reportsCodesPerPage');
    if (savedCodesPerPage) {
      setCodesPerPage(parseInt(savedCodesPerPage));
    }
  }, []);

  // حفظ إعدادات عدد الأكواد في الصفحة الواحدة
  useEffect(() => {
    localStorage.setItem('reportsCodesPerPage', codesPerPage.toString());
  }, [codesPerPage]);

  useEffect(() => {
    loadCodeUsage();
  }, [course.id]);

  const loadCodeUsage = async () => {
    try {
      setLoading(true);
      setError(null);
      const usage = await getCourseCodesUsage(course.id);
      setCodeUsage(usage);
    } catch (err) {
      console.error('Error loading code usage:', err);
      setError('حدث خطأ أثناء تحميل بيانات استخدام الأكواد');
    } finally {
      setLoading(false);
    }
  };

  const filteredCodes = codeUsage
    .filter(code => {
      // تطبيق فلتر البحث
      const searchMatch = 
        code.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.studentName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        code.studentEmail?.toLowerCase().includes(searchTerm.toLowerCase());
      
      // تطبيق فلتر الحالة
      if (filterUsed === 'used') {
        return searchMatch && code.isUsed;
      } else if (filterUsed === 'unused') {
        return searchMatch && !code.isUsed;
      } else {
        return searchMatch;
      }
    });

  const exportToCSV = () => {
    if (!course || filteredCodes.length === 0) return;
    
    const headers = ['الكود', 'حالة الاستخدام', 'اسم المستخدم', 'البريد الإلكتروني', 'تاريخ الاستخدام'];
    
    let csvContent = '\uFEFF'; // BOM للتأكد من دعم اللغة العربية
    
    // إضافة الرأس
    csvContent += headers.join(',') + '\n';
    
    // إضافة البيانات
    filteredCodes.forEach(code => {
      const row = [
        code.code,
        code.isUsed ? 'مستخدم' : 'غير مستخدم',
        code.studentName || '-',
        code.studentEmail || '-',
        code.usedAt ? format(code.usedAt, 'yyyy-MM-dd HH:mm', { locale: ar }) : '-'
      ];
      
      // معالجة الفواصل في البيانات
      const processedRow = row.map(cell => {
        // إذا كانت الخلية تحتوي على فاصلة، نضعها بين علامتي اقتباس
        if (cell.includes(',')) {
          return `"${cell}"`;
        }
        return cell;
      });
      
      csvContent += processedRow.join(',') + '\n';
    });
    
    // إنشاء رابط للتنزيل
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `تقرير-أكواد-${course.title}-${format(new Date(), 'yyyy-MM-dd')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportToPDF = () => {
    if (!course) return;
    
    // إنشاء مستند PDF بتوجيه أفقي
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4',
      compress: true
    });
    
    // استخدام الخط الافتراضي
    doc.setFont('helvetica');
    
    // إضافة عنوان للتقرير
    doc.setFontSize(16);
    doc.text('Course Codes Usage Report', 150, 10, { align: 'center' });
    doc.text(course.title, 150, 20, { align: 'center' });
    
    // إضافة تاريخ التقرير
    doc.setFontSize(10);
    doc.text(`Report Date: ${format(new Date(), 'yyyy-MM-dd HH:mm')}`, 150, 30, { align: 'center' });
    
    // إضافة إحصائيات
    doc.setFontSize(12);
    doc.text(`Total Codes: ${filteredCodes.length}`, 20, 40);
    doc.text(`Used Codes: ${filteredCodes.filter(code => code.isUsed).length}`, 20, 45);
    doc.text(`Unused Codes: ${filteredCodes.filter(code => !code.isUsed).length}`, 20, 50);
    doc.text(`Usage Rate: ${Math.round((filteredCodes.filter(code => code.isUsed).length / filteredCodes.length) * 100) || 0}%`, 20, 55);
    
    // إنشاء جدول البيانات باستخدام autoTable
    import('jspdf-autotable').then(x => {
      const { default: autoTable } = x;
      
      // تحويل البيانات إلى مصفوفة
      const tableColumn = ["Code", "Status", "Student Name", "Student Email", "Usage Date"];
      const tableRows = filteredCodes.map(code => {
        return [
          code.code,
          code.isUsed ? "Used" : "Unused",
          code.studentName || '-',
          code.studentEmail || '-',
          code.usedAt ? format(code.usedAt, 'yyyy-MM-dd HH:mm') : '-'
        ];
      });
      
      // إضافة الجدول إلى المستند
      autoTable(doc, {
        head: [tableColumn],
        body: tableRows,
        startY: 65,
        styles: { 
          halign: 'center',
          fontSize: 10
        },
        headStyles: { 
          fillColor: [41, 128, 185], 
          textColor: 255,
          fontSize: 12,
          halign: 'center'
        },
        alternateRowStyles: { fillColor: [240, 240, 240] },
        theme: 'grid',
        tableWidth: 'auto'
      });
      
      // التحقق إذا كان المستخدم يستخدم هاتف محمول
      const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
      
      // حفظ المستند
      if (isMobile) {
        // على الأجهزة المحمولة، قم بتحميل الملف مباشرة
        const pdfBlob = doc.output('blob');
        const url = URL.createObjectURL(pdfBlob);
        
        // إنشاء رابط وهمي للتحميل
        const link = document.createElement('a');
        link.href = url;
        link.download = `Course-Codes-Report-${course.title}-${format(new Date(), 'yyyy-MM-dd')}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        // على أجهزة الكمبيوتر، فتح الملف في نافذة جديدة
        window.open(URL.createObjectURL(doc.output('blob')));
      }
    });
  };

  const printCodes = () => {
    if (!course) return;
    
    // التحقق إذا كان المستخدم يستخدم هاتف محمول
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    const printWindow = window.open('', '_blank');
    if (!printWindow) return;
    
    const html = `
      <html>
        <head>
          <title>Course Codes Usage Report: ${course.title}</title>
          <meta charset="UTF-8">
          <meta name="viewport" content="width=device-width, initial-scale=1.0">
          <style>
            body { 
              font-family: Arial, sans-serif; 
              padding: 20px;
              direction: ${isMobile ? 'ltr' : 'rtl'};
            }
            h2, h4 { text-align: center; }
            table { 
              width: 100%; 
              border-collapse: collapse; 
              margin-top: 20px;
              page-break-inside: avoid;
            }
            th, td { 
              border: 1px solid #000; 
              padding: 8px; 
              text-align: center; 
            }
            th { 
              background-color: #f2f2f2; 
            }
            .header { 
              text-align: center; 
              margin-bottom: 20px; 
            }
            .stats { 
              display: flex; 
              justify-content: space-around; 
              margin: 20px 0; 
              flex-wrap: wrap;
            }
            .stat-box { 
              border: 1px solid #000; 
              padding: 10px; 
              text-align: center; 
              width: 23%;
              margin-bottom: 10px;
            }
            @media print {
              @page { size: landscape; }
              body { print-color-adjust: exact; -webkit-print-color-adjust: exact; }
            }
            @media (max-width: 768px) {
              .stats {
                flex-direction: column;
              }
              .stat-box {
                width: 100%;
                margin-bottom: 10px;
              }
              table {
                font-size: 12px;
              }
            }
            .print-buttons {
              display: flex;
              justify-content: center;
              margin: 20px 0;
              gap: 10px;
            }
            .print-buttons button {
              padding: 8px 16px;
              border: none;
              border-radius: 4px;
              cursor: pointer;
              font-weight: bold;
            }
            .print-button {
              background-color: #4CAF50;
              color: white;
            }
            .close-button {
              background-color: #f44336;
              color: white;
            }
            @media print {
              .print-buttons {
                display: none;
              }
            }
          </style>
        </head>
        <body>
          <div class="print-buttons">
            <button class="print-button" onclick="window.print()">Print Report</button>
            <button class="close-button" onclick="window.close()">Close</button>
          </div>
          
          <div class="header">
            <h2>${isMobile ? 'Course Codes Usage Report: ' : 'تقرير استخدام أكواد الدورة: '}${course.title}</h2>
            <p>${isMobile ? 'Report Date: ' : 'تاريخ التقرير: '}${format(new Date(), 'yyyy-MM-dd HH:mm', { locale: isMobile ? undefined : ar })}</p>
          </div>
          
          <div class="stats">
            <div class="stat-box">
              <h4>${isMobile ? 'Total Codes' : 'إجمالي الأكواد'}</h4>
              <p>${filteredCodes.length}</p>
            </div>
            <div class="stat-box">
              <h4>${isMobile ? 'Used Codes' : 'الأكواد المستخدمة'}</h4>
              <p>${filteredCodes.filter(code => code.isUsed).length}</p>
            </div>
            <div class="stat-box">
              <h4>${isMobile ? 'Unused Codes' : 'الأكواد غير المستخدمة'}</h4>
              <p>${filteredCodes.filter(code => !code.isUsed).length}</p>
            </div>
            <div class="stat-box">
              <h4>${isMobile ? 'Usage Rate' : 'نسبة الاستخدام'}</h4>
              <p>${Math.round((filteredCodes.filter(code => code.isUsed).length / filteredCodes.length) * 100) || 0}%</p>
            </div>
          </div>
          
          <table>
            <thead>
              <tr>
                <th>#</th>
                <th>${isMobile ? 'Code' : 'الكود'}</th>
                <th>${isMobile ? 'Status' : 'الحالة'}</th>
                <th>${isMobile ? 'Student Name' : 'اسم الطالب'}</th>
                <th>${isMobile ? 'Email' : 'البريد الإلكتروني'}</th>
                <th>${isMobile ? 'Usage Date' : 'تاريخ الاستخدام'}</th>
              </tr>
            </thead>
            <tbody>
              ${filteredCodes.map((code, index) => {
                return `
                  <tr>
                    <td>${index + 1}</td>
                    <td>${code.code}</td>
                    <td>${code.isUsed ? (isMobile ? 'Used' : 'مستخدم') : (isMobile ? 'Unused' : 'غير مستخدم')}</td>
                    <td>${code.studentName || '-'}</td>
                    <td>${code.studentEmail || '-'}</td>
                    <td>${code.usedAt ? format(code.usedAt, 'yyyy-MM-dd HH:mm', { locale: isMobile ? undefined : ar }) : '-'}</td>
                  </tr>
                `;
              }).join('')}
            </tbody>
          </table>
          
          <script>
            // تنفيذ الطباعة تلقائيًا على الأجهزة المحمولة بعد تحميل الصفحة
            window.onload = function() {
              setTimeout(function() {
                if (${isMobile}) {
                  window.print();
                }
              }, 1000);
            };
          </script>
        </body>
      </html>
    `;
    
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
  };

  const getUsageStats = () => {
    const total = filteredCodes.length;
    const used = filteredCodes.filter(code => code.isUsed).length;
    const unused = total - used;
    const usagePercentage = total > 0 ? Math.round((used / total) * 100) : 0;
    
    return { total, used, unused, usagePercentage };
  };

  const stats = getUsageStats();

  const indexOfLastCode = currentPage * codesPerPage;
  const indexOfFirstCode = indexOfLastCode - codesPerPage;
  const currentCodes = filteredCodes.slice(indexOfFirstCode, indexOfLastCode);

  const pageNumbers = [];
  for (let i = 1; i <= Math.ceil(filteredCodes.length / codesPerPage); i++) {
    pageNumbers.push(i);
  }

  return (
    <Container className="mb-4">
      <Row className="d-flex justify-content-between align-items-center bg-primary text-white">
        <div>
          <FaChartBar className="me-2" />
          تقارير استخدام الأكواد
        </div>
      </Row>
      <Row className="mt-4">
        {loading ? (
          <div className="text-center py-5">
            <Spinner animation="border" variant="primary" />
            <p className="mt-2">جاري تحميل البيانات...</p>
          </div>
        ) : error ? (
          <Alert variant="danger">{error}</Alert>
        ) : (
          <>
            {/* إحصائيات الاستخدام */}
            <Row className="mb-4">
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h6>إجمالي الأكواد</h6>
                    <h3>{stats.total}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h6>الأكواد المستخدمة</h6>
                    <h3>{stats.used}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h6>الأكواد غير المستخدمة</h6>
                    <h3>{stats.unused}</h3>
                  </Card.Body>
                </Card>
              </Col>
              <Col md={3}>
                <Card className="text-center">
                  <Card.Body>
                    <h6>نسبة الاستخدام</h6>
                    <h3>{stats.usagePercentage}%</h3>
                  </Card.Body>
                </Card>
              </Col>
            </Row>

            {/* أدوات البحث والتصفية */}
            <Row className="mb-3">
              <Col md={4}>
                <Form.Group>
                  <Form.Control
                    type="text"
                    placeholder="بحث بالكود أو اسم الطالب أو البريد الإلكتروني"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Select
                  value={filterUsed}
                  onChange={(e) => setFilterUsed(e.target.value)}
                >
                  <option value="all">جميع الأكواد</option>
                  <option value="used">الأكواد المستخدمة</option>
                  <option value="unused">الأكواد غير المستخدمة</option>
                </Form.Select>
              </Col>
              <Col md={2}>
                <Form.Select
                  value={codesPerPage}
                  onChange={(e) => {
                    setCodesPerPage(parseInt(e.target.value));
                    setCurrentPage(1); // إعادة تعيين الصفحة الحالية عند تغيير عدد الأكواد
                  }}
                >
                  <option value="10">10 أكواد</option>
                  <option value="50">50 كود</option>
                  <option value="100">100 كود</option>
                  <option value="150">150 كود</option>
                  <option value="200">200 كود</option>
                  <option value="300">300 كود</option>
                  <option value="400">400 كود</option>
                  <option value="500">500 كود</option>
                </Form.Select>
              </Col>
              <Col md={3} className="text-end">
                <ButtonGroup>
                  <Button
                    variant="success"
                    onClick={exportToCSV}
                    disabled={filteredCodes.length === 0}
                  >
                    <FaFileDownload className="me-1" />
                    تصدير CSV
                  </Button>
                  <Button
                    variant="primary"
                    onClick={exportToPDF}
                    disabled={filteredCodes.length === 0}
                  >
                    <FaFilePdf className="me-1" />
                    تصدير PDF
                  </Button>
                  <Button
                    variant="info"
                    onClick={printCodes}
                    disabled={filteredCodes.length === 0}
                  >
                    <FaPrint className="me-1" />
                    الطباعة
                  </Button>
                </ButtonGroup>
              </Col>
            </Row>

            {/* جدول الأكواد */}
            {filteredCodes.length === 0 ? (
              <Alert variant="info">لا توجد بيانات متطابقة مع معايير البحث</Alert>
            ) : (
              <Table responsive striped bordered hover>
                <thead>
                  <tr>
                    <th>الكود</th>
                    <th>الحالة</th>
                    <th>اسم الطالب</th>
                    <th>البريد الإلكتروني</th>
                    <th>تاريخ الاستخدام</th>
                    <th>تاريخ الإنشاء</th>
                    <th>تاريخ الانتهاء</th>
                  </tr>
                </thead>
                <tbody>
                  {currentCodes.map((code) => (
                    <tr key={code.id}>
                      <td>{code.code}</td>
                      <td>
                        {code.isUsed ? (
                          <Badge bg="success">مستخدم</Badge>
                        ) : (
                          <Badge bg="secondary">غير مستخدم</Badge>
                        )}
                      </td>
                      <td>{code.studentName || '-'}</td>
                      <td>{code.studentEmail || '-'}</td>
                      <td>
                        {code.usedAt
                          ? format(code.usedAt, 'yyyy-MM-dd HH:mm', { locale: ar })
                          : '-'}
                      </td>
                      <td>{format(code.createdAt, 'yyyy-MM-dd HH:mm', { locale: ar })}</td>
                      <td>
                        {code.expiresAt
                          ? format(code.expiresAt, 'yyyy-MM-dd HH:mm', { locale: ar })
                          : 'غير محدد'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
            <Row className="mt-4">
              <Col md={6}>
                <Form.Select
                  value={codesPerPage}
                  onChange={(e) => setCodesPerPage(parseInt(e.target.value))}
                >
                  <option value={10}>10 أكواد</option>
                  <option value={20}>20 كود</option>
                  <option value={50}>50 كود</option>
                </Form.Select>
              </Col>
              <Col md={6} className="text-end">
                <nav aria-label="Page navigation example">
                  <ul className="pagination">
                    {pageNumbers.map((pageNumber) => (
                      <li key={pageNumber} className={`page-item ${currentPage === pageNumber ? 'active' : ''}`}>
                        <button className="page-link" onClick={() => setCurrentPage(pageNumber)}>
                          {pageNumber}
                        </button>
                      </li>
                    ))}
                  </ul>
                </nav>
              </Col>
            </Row>
          </>
        )}
      </Row>
    </Container>
  );
};

export default CourseCodesReports;
